import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../i18n'
import { useApi, apiPost } from '../hooks/useApi'
import { useAuth } from '../context/AuthContext'
import type { Test, Package, BulkTagResult } from '../types'

interface TestBrowserProps {
  projectId: string
  onTestsAssigned: () => void
  onClose: () => void
}

export function TestBrowser({ projectId, onTestsAssigned, onClose }: TestBrowserProps) {
  const { t } = useTranslation()
  const { token } = useAuth()
  const [packageFilter, setPackageFilter] = useState('')
  const [testNameFilter, setTestNameFilter] = useState('')
  const [selectedTests, setSelectedTests] = useState<Set<string>>(new Set())
  const [assigning, setAssigning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const packages = useApi<Package[]>(`/tests/packages?projectId=${projectId}`)
  const [tests, setTests] = useState<Test[]>([])
  const [loadingTests, setLoadingTests] = useState(false)

  const fetchTests = useCallback(async () => {
    setLoadingTests(true)
    try {
      const params = new URLSearchParams()
      if (packageFilter) {
        params.set('package', packageFilter)
      }
      if (testNameFilter) {
        params.set('testName', testNameFilter)
      }
      const response = await fetch(`/api/tests/assignable/${projectId}?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!response.ok) {
        throw new Error(t('error.failedToFetchTests'))
      }
      const data = await response.json()
      setTests(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.failedToLoadTests'))
    } finally {
      setLoadingTests(false)
    }
  }, [projectId, packageFilter, testNameFilter, token, t])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Fetching from external system on dependency change
    fetchTests()
  }, [fetchTests])

  const [syncedProjectId, setSyncedProjectId] = useState(projectId)
  if (projectId !== syncedProjectId) {
    setSyncedProjectId(projectId)
    setTestNameFilter('')
    setPackageFilter('')
    setSelectedTests(new Set())
  }

  const filterKey = `${packageFilter} ${testNameFilter}`
  const [syncedFilterKey, setSyncedFilterKey] = useState(filterKey)
  if (filterKey !== syncedFilterKey) {
    setSyncedFilterKey(filterKey)
    setSelectedTests(new Set())
  }

  const handleSelectAll = () => {
    setSelectedTests(new Set(tests.map((t) => t.id)))
  }

  const handleSelectNone = () => {
    setSelectedTests(new Set())
  }

  const handleToggleTest = (testId: string) => {
    setSelectedTests((prev) => {
      const next = new Set(prev)
      if (next.has(testId)) {
        next.delete(testId)
      } else {
        next.add(testId)
      }
      return next
    })
  }

  const handleAssign = async () => {
    if (selectedTests.size === 0) return

    setAssigning(true)
    setError(null)

    try {
      const testIds = Array.from(selectedTests).map((id) => {
        const test = tests.find((t) => t.id === id)
        if (!test) throw new Error('Test not found')
        return { className: test.className, methodName: test.methodName }
      })

      const result = await apiPost<BulkTagResult>('/tests/bulk-tag', {
        testIds,
        projectId,
      })

      if (result.failed > 0) {
        setError(`${result.tagged} tests assigned, ${result.failed} failed`)
      }

      onTestsAssigned()
      fetchTests()
      setSelectedTests(new Set())
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.failedToFetchTests'))
    } finally {
      setAssigning(false)
    }
  }

  return (
    <div data-testid="test-browser" className="card" style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontWeight: 600 }}>{t('component.addTestsToProject')}</h3>
        <button
          onClick={onClose}
          className="btn btn-secondary btn-sm"
          data-testid="close-browser"
          aria-label={t('button.close')}
          title={t('button.close')}
        >
          <i className="pi pi-times" aria-hidden="true" style={{ marginRight: '0.25rem' }} />
          {t('button.close')}
        </button>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <label htmlFor="package-filter" style={{ display: 'block', marginBottom: '0.5rem' }}>
          {t('label.filterByPackage')}
        </label>
        <select
          id="package-filter"
          value={packageFilter}
          onChange={(e) => setPackageFilter(e.target.value)}
          data-testid="package-filter"
          className="input"
        >
          <option value="">{t('label.allPackages')}</option>
          {packages.data?.map((pkg) => (
            <option key={pkg.name} value={pkg.name}>
              {pkg.name} ({pkg.testCount})
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <label htmlFor="test-name-filter" style={{ display: 'block', marginBottom: '0.5rem' }}>
          {t('label.filterByTestName')}
        </label>
        <input
          id="test-name-filter"
          type="text"
          className="input"
          placeholder={t('placeholder.testNameFilter')}
          value={testNameFilter}
          onChange={(e) => setTestNameFilter(e.target.value)}
          data-testid="test-name-filter"
        />
      </div>

      {error && (
        <div
          style={{
            marginTop: '1rem',
            padding: '0.5rem',
            background: 'var(--color-error-light)',
            color: 'var(--color-error)',
            borderRadius: '4px',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ marginTop: '1rem' }}>
        <div
          style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}
        >
          <button
            onClick={handleSelectAll}
            className="btn btn-secondary btn-sm"
            data-testid="select-all"
            disabled={tests.length === 0}
            aria-label={t('button.selectAll')}
            title={t('button.selectAll')}
          >
            <i
              className="pi pi-check-square"
              aria-hidden="true"
              style={{ marginRight: '0.25rem' }}
            />
            {t('button.selectAll')}
          </button>
          <button
            onClick={handleSelectNone}
            className="btn btn-secondary btn-sm"
            data-testid="select-none"
            disabled={selectedTests.size === 0}
            aria-label={t('button.selectNone')}
            title={t('button.selectNone')}
          >
            <i className="pi pi-stop" aria-hidden="true" style={{ marginRight: '0.25rem' }} />
            {t('button.selectNone')}
          </button>
          <span style={{ color: 'var(--color-gray-400)', fontSize: '0.875rem' }}>
            {t('label.testsSelected', { count: `${selectedTests.size} / ${tests.length}` })}
          </span>
        </div>

        <div
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid var(--color-border)',
            borderRadius: '4px',
          }}
        >
          {loadingTests ? (
            <div
              style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-gray-400)' }}
              data-testid="loading-tests"
            >
              {t('label.loading')}
            </div>
          ) : tests.length === 0 ? (
            <div
              style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-gray-400)' }}
              data-testid="no-tests"
            >
              {t('empty.noTests')}
            </div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="test-list">
              {tests.map((test) => (
                <li
                  key={test.id}
                  style={{
                    padding: '0.5rem 1rem',
                    borderBottom: '1px solid var(--color-border)',
                    cursor: 'pointer',
                    background: selectedTests.has(test.id)
                      ? 'rgba(37, 99, 235, 0.1)'
                      : 'var(--color-surface)',
                  }}
                  onClick={() => handleToggleTest(test.id)}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTests.has(test.id)}
                      onChange={() => handleToggleTest(test.id)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      <span>{test.className}</span>
                      <span style={{ color: 'var(--color-gray-400)' }}>.{test.methodName}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <button
          onClick={handleAssign}
          className="btn btn-primary"
          disabled={selectedTests.size === 0 || assigning}
          data-testid="assign-tests"
          aria-label={t('button.assign')}
          title={t('button.assign')}
        >
          <i className="pi pi-plus" aria-hidden="true" style={{ marginRight: '0.5rem' }} />
          {assigning ? t('misc.assigning') : `${t('button.assign')} (${selectedTests.size})`}
        </button>
      </div>
    </div>
  )
}
